import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  useRemoveOrganizationLogo,
  useUpdateOrganizationDefaultReportCustomText,
  useUploadOrganizationLogo,
} from '../../../../../../hooks/api/organization';
import { useUpdateProjectReportCustomText } from '../../../../../../hooks/api/projects';
import { useUpdateReleaseGroupReportCustomText } from '../../../../../../hooks/api/releaseGroups';
import * as ReportGenerationStore from '../../../../../../redux/reportGeneration';
import type { ReportLogoAction } from '../types';

/**
 * Parameters for project-level report settings.
 */
type ProjectParams = {
  /** Project locator string (e.g., "git+github.com/owner/repo$revision") */
  projectLocator: string;
  /** Must be undefined for project params (discriminated union) */
  releaseGroupId?: never;
};

/**
 * Parameters for release group-level report settings.
 */
type ReleaseGroupParams = {
  /** Must be undefined for release group params (discriminated union) */
  projectLocator?: never;
  /** Release group ID for release group-level reports */
  releaseGroupId: number;
};

/**
 * Common parameters for saving report settings.
 */
type CommonParams = {
  /** Organization ID required for logo upload/removal operations */
  organizationId: number;
};

/**
 * Combined parameters for saving report settings.
 * Uses discriminated union to ensure either projectLocator OR releaseGroupId is provided, but never both.
 */
type SaveReportSettingsParams = (ProjectParams | ReleaseGroupParams) & CommonParams;

/**
 * Parameters passed to the onSave callback function.
 */
type OnSaveParams = {
  /** Whether to set this text as the organization default */
  shouldSetDefault: boolean;
  /** File to upload for logo, or null if no file */
  file: File | null;
  /** Action to perform on the logo (save/remove) */
  reportLogoAction: ReportLogoAction | null;
  /** The custom text content for the report */
  customText: string;
  /** Whether to propagate organization default text to existing projects/release groups */
  shouldPropagate: boolean;
};

/**
 * Hook for saving report settings including logo upload/removal, organization defaults, and custom text.
 *
 * This hook consolidates the logic shared between project and release group report settings,
 * eliminating code duplication and providing a consistent interface. It handles:
 * - Logo upload and removal
 * - Organization default text updates
 * - Project/release group specific text updates (only when changed)
 * - Redux state updates
 *
 * The hook uses discriminated union types to ensure type safety - you must provide either
 * a projectLocator OR a releaseGroupId, but never both.
 *
 * @param params - Configuration parameters
 * @param params.organizationId - Organization ID required for logo operations
 * @param params.projectLocator - Project locator string (for project-level reports)
 * @param params.releaseGroupId - Release group ID (for release group-level reports)
 *
 * @example
 * ```typescript
 * // For project-level reports
 * const onSave = useSaveReportSettings({
 *   projectLocator: "git+github.com/owner/repo$revision",
 *   organizationId: 123
 * });
 *
 * // For release group-level reports
 * const onSave = useSaveReportSettings({
 *   releaseGroupId: 456,
 *   organizationId: 123
 * });
 * ```
 *
 * @returns A callback function that can be passed to the Panel component as onSave
 */
export default function useSaveReportSettings({
  organizationId,
  ...entityParams
}: SaveReportSettingsParams) {
  const dispatch = useDispatch();
  const originalCustomText = useSelector(ReportGenerationStore.selectors.getCustomHeaderText);
  // Destructure mutateAsync functions from all the hooks we need
  const { mutateAsync: uploadLogo } = useUploadOrganizationLogo();
  const { mutateAsync: removeLogo } = useRemoveOrganizationLogo();
  const { mutateAsync: updateOrgDefaultText } = useUpdateOrganizationDefaultReportCustomText();
  const { mutateAsync: updateProjectText } = useUpdateProjectReportCustomText();
  const { mutateAsync: updateReleaseGroupText } = useUpdateReleaseGroupReportCustomText();

  return useCallback(async ({
    shouldSetDefault,
    file,
    reportLogoAction,
    customText,
    shouldPropagate,
  }: OnSaveParams): Promise<void> => {
    try {
      // Handle logo upload/removal
      if (reportLogoAction === 'SAVE' && file) {
        await uploadLogo({ organizationId, file });
      }
      if (reportLogoAction === 'REMOVE') {
        await removeLogo();
      }

      // Handle default organization report text
      if (shouldSetDefault) {
        await updateOrgDefaultText({
          organizationId,
          reportCustomText: customText,
          shouldPropagate,
        });
      }

      // Update the specific project/release group custom text only if it has changed
      if (customText !== originalCustomText) {
        if ('projectLocator' in entityParams && entityParams.projectLocator) {
          // Project-level report
          await updateProjectText({
            projectLocator: entityParams.projectLocator,
            reportCustomText: customText,
          });
        } else if ('releaseGroupId' in entityParams && entityParams.releaseGroupId) {
          // Release group-level report
          await updateReleaseGroupText({
            releaseGroupId: entityParams.releaseGroupId,
            reportCustomText: customText,
          });
        }
      }

      // Update Redux state with the new custom text
      dispatch(ReportGenerationStore.actions.setCustomHeaderText(customText));
    } catch (e) {
      // Error handling is done by useToastedMutation in the individual hooks
    }
  }, [
    originalCustomText,
    organizationId,
    dispatch,
    entityParams,
    uploadLogo,
    removeLogo,
    updateOrgDefaultText,
    updateProjectText,
    updateReleaseGroupText,
  ]);
}
